import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { Users, Video, BarChart3, Shield, DollarSign, Activity, RefreshCw, Download, TrendingUp, Star, Key, Zap, ArrowUpCircle, Globe, Sparkles, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { SeedTab } from '../components/SeedTab';
import { FeaturedQueueTab } from '../components/FeaturedQueueTab';
import { UploadEventsTab } from '../components/UploadEventsTab';
import { LikePromptAbTab } from '../components/LikePromptAbTab';
import { hasVideoFile } from '../utils/hlsPlayer';
import { ADMIN_EMAILS, API, CopyEmailsButton, TabButton } from '../components/admin/adminShared';
import { AdminOverviewTab } from '../components/admin/AdminOverviewTab';
import { AdminInvitesTab } from '../components/admin/AdminInvitesTab';
import { AdminUpgradesTab } from '../components/admin/AdminUpgradesTab';
import { AdminModerationTab } from '../components/admin/AdminModerationTab';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminVideosTab } from '../components/admin/AdminVideosTab';
import { AdminRevenueTab } from '../components/admin/AdminRevenueTab';
import { AdminActivityTab } from '../components/admin/AdminActivityTab';
import { AdminTrafficTab } from '../components/admin/AdminTrafficTab';
import { AdminFlagsTab } from '../components/admin/AdminFlagsTab';
import { AdminPlatformAnalyticsTab } from '../components/admin/AdminPlatformAnalyticsTab';
import { AdminVideoPreviewModal } from '../components/admin/AdminVideoPreviewModal';
import { AdminVideoDiagnoseModal } from '../components/admin/AdminVideoDiagnoseModal';

export const AdminPage = () => {
  const { user, token, axiosInstance } = useAuth();
  const { refreshFlags: refreshGlobalFlags } = useFeatureFlags();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [pendingVideos, setPendingVideos] = useState([]);
  const [flaggedContent, setFlaggedContent] = useState([]);
  const [loginActivity, setLoginActivity] = useState({ daily: [], recent: [] });
  const [visitorStats, setVisitorStats] = useState({ summary: {}, daily: [], top_pages: [], top_referrers: [] });
  // 24/7 live channel analytics (iter 121). Same shape as visitorStats but
  // for the /live surface. Loaded alongside visitor stats when the traffic
  // tab opens.
  const [channelStats, setChannelStats] = useState({ summary: {}, referrer_split: [], exit_videos: [], daily: [] });
  const [visitorRange, setVisitorRange] = useState(30); // 1 | 7 | 30 | 90 | 365 — controls the daily-breakdown chart
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Invite codes state
  const [inviteCodes, setInviteCodes] = useState([]);
  const [newCode, setNewCode] = useState('');
  const [newMaxUses, setNewMaxUses] = useState(10);
  const [newRoleType, setNewRoleType] = useState('listener');
  const [codeLoading, setCodeLoading] = useState(false);
  
  // Upgrade requests state
  const [upgradeRequests, setUpgradeRequests] = useState([]);
  
  // Video reports state
  const [videoReports, setVideoReports] = useState([]);
  
  // Feature flags state
  const [featureFlags, setFeatureFlags] = useState({});
  const [flagHistory, setFlagHistory] = useState([]);
  const [flagEnvironment, setFlagEnvironment] = useState('development');
  const [flagCategoryFilter, setFlagCategoryFilter] = useState('all');
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [statusFilter, setStatusFilter] = useState('active'); // active, all, pending, approved, rejected, deleted
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all'); // all, listener, artist (Users tab only)
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Video preview modal — lets admin actually watch flagged / pending videos
  // before approving or rejecting them.
  const [previewVideo, setPreviewVideo] = useState(null);

  // Track admin API health so we can show ONE clear banner instead of
  // misleading fake numbers when the user's token has expired or the backend
  // is unreachable. Previous behaviour was to silently fall back to mocked
  // data (e.g. totalViews: 125000) which made the dashboard *look* fine while
  // every real call was 401-ing. That hid the real problem from the admin
  // and cost them a Slack thread to debug.
  const [adminApiError, setAdminApiError] = useState(null); // null | 'unauthorized' | 'network'

  // Growth funnel: signups → publishers → founders, all-time + last 30 days.
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const fetchFunnel = useCallback(async () => {
    setFunnelLoading(true);
    try {
      const res = await axiosInstance.get('/admin/metrics/signups-uploads-founders');
      setFunnel(res.data);
    } catch (err) {
      // 404 means the endpoint isn't deployed yet on production — show nothing
      // rather than a scary error, but log so we notice.
      console.warn('Funnel metrics unavailable:', err?.response?.status);
      setFunnel(null);
    } finally {
      setFunnelLoading(false);
    }
  }, [axiosInstance]);
  useEffect(() => {
    if (activeTab === 'overview' && user && ADMIN_EMAILS.includes(user.email)) {
      fetchFunnel();
    }
  }, [activeTab, user, fetchFunnel]);

  // Close preview on Esc
  useEffect(() => {
    if (!previewVideo) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setPreviewVideo(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewVideo]);

  // Check if user is admin
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  // Download a CSV of all signed-up users. Admin-only on both the
  // backend and frontend.
  const handleExportUsers = async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${API}/api/admin/users/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const filename = `audivue_users_${today}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Users exported');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  // Download the "ghost artists" outreach CSV — artists who signed up
  // but haven't uploaded anything yet. Each row includes a pre-written,
  // personalized re-engagement message body so admin can copy-paste
  // (or bulk mail-merge) into their mail client. Default 30-day window.
  const handleExportGhostArtists = async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${API}/api/admin/artists/ghost-outreach.csv?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Export failed (${res.status})`);
      }
      const ghostCount = res.headers.get('X-Ghost-Artist-Count') || '?';
      const blob = await res.blob();
      const today = new Date().toISOString().slice(0, 10);
      const filename = `audivue_ghost_artists_${today}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        ghostCount === '0'
          ? 'No ghost artists in the last 30 days.'
          : `Downloaded ${ghostCount} ghost artist${ghostCount === '1' ? '' : 's'}.`,
      );
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchUsers(),
        fetchVideos(),
        fetchPendingVideos(),
        fetchActivityLog(),
        fetchVisitorStats(),
        fetchInviteCodes(),
        fetchFeatureFlags(),
        fetchUpgradeRequests(),
        fetchVideoReports()
      ]);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };
  // Ref keeps the effect on the latest fetchAllData without re-running on every render.
  const fetchAllDataRef = useRef(fetchAllData);
  fetchAllDataRef.current = fetchAllData;

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (user && !isAdmin) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
      return;
    }
    fetchAllDataRef.current();
  }, [token, user, isAdmin, navigate]);

  const fetchVideoReports = async () => {
    try {
      const res = await axiosInstance.get('/admin/reports?status=pending');
      setVideoReports(res.data || []);
    } catch (err) {
      console.error('Failed to fetch video reports:', err);
      setVideoReports([]);
    }
  };

  const handleDismissReport = async (reportId) => {
    try {
      await axiosInstance.post(`/admin/reports/${reportId}/dismiss`);
      toast.success('Report dismissed');
      fetchVideoReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to dismiss report');
    }
  };

  const handleReportAction = async (reportId, action) => {
    try {
      await axiosInstance.post(`/admin/reports/${reportId}/action?action=${action}`);
      toast.success(`Action '${action.replace('_', ' ')}' taken`);
      fetchVideoReports();
      fetchVideos();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to take action');
    }
  };

  const fetchUpgradeRequests = async () => {
    try {
      const res = await axiosInstance.get('/admin/upgrade-requests?status=pending');
      setUpgradeRequests(res.data || []);
    } catch (err) {
      console.error('Failed to fetch upgrade requests:', err);
      setUpgradeRequests([]);
    }
  };

  const handleApproveUpgrade = async (requestId) => {
    try {
      await axiosInstance.post(`/admin/upgrade-requests/${requestId}/approve`);
      toast.success('Upgrade approved');
      fetchUpgradeRequests();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve upgrade');
    }
  };

  const handleRejectUpgrade = async (requestId) => {
    try {
      await axiosInstance.post(`/admin/upgrade-requests/${requestId}/reject`);
      toast.success('Upgrade rejected');
      fetchUpgradeRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject upgrade');
    }
  };

  const handleSetUserRole = async (userId, role) => {
    try {
      await axiosInstance.post(`/admin/users/${userId}/set-role?role=${role}`);
      toast.success(`User role set to ${role}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set user role');
    }
  };

  const fetchFeatureFlags = async () => {
    try {
      const res = await axiosInstance.get('/admin/feature-flags');
      setFeatureFlags(res.data.flags || {});
      setFlagEnvironment(res.data.environment || 'development');
      
      // Fetch history
      const historyRes = await axiosInstance.get('/admin/feature-flags/history?limit=20');
      setFlagHistory(historyRes.data.history || []);
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
    }
  };

  const toggleFeatureFlag = async (flagKey, currentValue) => {
    try {
      await axiosInstance.put(`/admin/feature-flags/${flagKey}?enabled=${!currentValue}`);
      toast.success(`${flagKey} ${!currentValue ? 'enabled' : 'disabled'}`);
      fetchFeatureFlags();
      refreshGlobalFlags(); // Update global context
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update flag');
    }
  };

  const resetFeatureFlag = async (flagKey) => {
    try {
      await axiosInstance.post(`/admin/feature-flags/${flagKey}/reset`);
      toast.success(`${flagKey} reset to default`);
      fetchFeatureFlags();
      refreshGlobalFlags();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset flag');
    }
  };

  const rollbackFeatureFlag = async (flagKey) => {
    try {
      await axiosInstance.post(`/admin/feature-flags/${flagKey}/rollback`);
      toast.success(`${flagKey} rolled back to previous value`);
      fetchFeatureFlags();
      refreshGlobalFlags();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rollback flag');
    }
  };

  const fetchInviteCodes = async () => {
    try {
      const res = await axiosInstance.get('/admin/invite-codes');
      setInviteCodes(res.data || []);
    } catch (err) {
      console.error('Failed to fetch invite codes:', err);
      setInviteCodes([]);
    }
  };

  const createInviteCode = async () => {
    if (!newCode.trim()) {
      toast.error('Please enter a code');
      return;
    }
    setCodeLoading(true);
    try {
      await axiosInstance.post('/admin/invite-codes', {
        code: newCode.trim(),
        max_uses: newMaxUses,
        role_type: newRoleType
      });
      toast.success(`${newRoleType === 'artist' ? 'Artist' : 'Listener'} invite code created`);
      setNewCode('');
      setNewMaxUses(10);
      setNewRoleType('listener');
      fetchInviteCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create code');
    } finally {
      setCodeLoading(false);
    }
  };

  const toggleCodeActive = async (codeId, currentActive) => {
    try {
      await axiosInstance.put(`/admin/invite-codes/${codeId}`, {
        active: !currentActive
      });
      toast.success(`Code ${currentActive ? 'disabled' : 'enabled'}`);
      fetchInviteCodes();
    } catch (err) {
      toast.error('Failed to update code');
    }
  };

  const deleteInviteCode = async (codeId) => {
    if (!window.confirm('Delete this invite code?')) return;
    try {
      await axiosInstance.delete(`/admin/invite-codes/${codeId}`);
      toast.success('Code deleted');
      fetchInviteCodes();
    } catch (err) {
      toast.error('Failed to delete code');
    }
  };

  // Copy invite link to clipboard
  const copyInviteLink = (code) => {
    // Use the current domain (will be audivue.io in production)
    const baseUrl = window.location.origin;
    const inviteLink = `${baseUrl}/j/${code}`;
    navigator.clipboard.writeText(inviteLink);
    toast.success('Invite link copied!', {
      description: inviteLink
    });
  };

  const fetchStats = async () => {
    try {
      const res = await axiosInstance.get('/admin/stats');
      setStats(res.data);
    } catch (err) {
      // Honest zero rather than the old `totalViews: 125000` mock — that made
      // a broken dashboard look healthy. Surface the failure to the user via
      // adminApiError so they can act on it.
      const status = err?.response?.status;
      if (status === 401 || status === 403) setAdminApiError('unauthorized');
      else setAdminApiError('network');
      setStats({
        totalUsers: 0,
        totalVideos: 0,
        totalViews: 0,
        totalRevenue: 0,
        pendingReviews: 0,
        flaggedContent: 0,
        activeCreators: 0,
        newUsersToday: 0,
      });
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axiosInstance.get('/admin/users');
      setUsers(res.data);
    } catch {
      // Fallback - fetch from regular endpoint
      try {
        const res = await axiosInstance.get('/users');
        setUsers(res.data || []);
      } catch {
        setUsers([]);
      }
    }
  };

  const fetchVideos = async () => {
    try {
      // Admin needs to see EVERYTHING (active + deleted + rebuilt). 1000 is
      // a generous cap; if the catalog grows past that we'll switch to
      // pagination, but for now 1000 covers any realistic library.
      const res = await axiosInstance.get('/videos', { params: { limit: 1000, include_all: true } });
      setVideos(res.data || []);
    } catch (err) {
      console.error('Admin fetchVideos error:', err);
      setVideos([]);
    }
  };

  const fetchPendingVideos = async () => {
    try {
      const res = await axiosInstance.get('/admin/videos/pending');
      setPendingVideos(res.data || []);
    } catch {
      // Filter from all videos
      const pending = videos.filter(v => v.status === 'pending_review' || v.status === 'processing');
      setPendingVideos(pending);
    }
  };

  const fetchActivityLog = async () => {
    try {
      const res = await axiosInstance.get('/admin/login-activity?days=30');
      setLoginActivity(res.data || { daily: [], recent: [] });
    } catch {
      setLoginActivity({ daily: [], recent: [] });
    }
  };

  // `hideMyVisits` — when true, the dashboard asks the backend to exclude
  // every IP that has ever logged in (admins + creators). When false (default)
  // every visit is counted. This used to be a DESTRUCTIVE button that deleted
  // rows from the DB; making it a non-destructive filter prevents the
  // "everything went to 0" data-loss bug.
  const [hideMyVisits, setHideMyVisits] = useState(false);

  const fetchVisitorStats = useCallback(async (rangeDays, hideMine) => {
    const days = rangeDays || visitorRange || 30;
    const includeSelf = !(hideMine ?? hideMyVisits);
    try {
      const res = await axiosInstance.get(`/admin/visitors?days=${days}&include_self=${includeSelf}`);
      setVisitorStats(res.data || { summary: {}, daily: [], top_pages: [], top_referrers: [] });
    } catch {
      setVisitorStats({ summary: {}, daily: [], top_pages: [], top_referrers: [] });
    }
    // Piggyback the 24/7 channel analytics fetch on the same trigger — same
    // window/self-exclude semantics as visitor stats so the two numbers are
    // apples-to-apples comparable.
    try {
      const res2 = await axiosInstance.get(`/admin/channel/analytics?days=${days}&include_self=${includeSelf}`);
      setChannelStats(res2.data || { summary: {}, referrer_split: [], exit_videos: [], daily: [] });
    } catch {
      setChannelStats({ summary: {}, referrer_split: [], exit_videos: [], daily: [] });
    }
  }, [axiosInstance, visitorRange, hideMyVisits]);

  // One-click scrub of all historical bot/AI-agent visits — used after we
  // broaden the bot filter to catch new agentic crawlers. DESTRUCTIVE,
  // requires explicit confirm.
  const scrubBotVisits = async () => {
    if (!window.confirm("⚠️ PERMANENTLY DELETE all visits from ChatGPT, Claude, Perplexity, GPTBot, Twitterbot, Facebook scraper, and other known crawlers?\n\nThis cannot be undone. Only bot rows are removed — real visitors are untouched.")) return;
    try {
      const res = await axiosInstance.post('/admin/visitors/scrub-bots');
      toast.success(`Cleaned ${res.data.matched} bot visit(s) + ${res.data.empty} empty-UA row(s).`);
      fetchVisitorStats(visitorRange);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bot scrub failed.');
    }
  };

  // Re-fetch traffic stats whenever the chart range OR the hide-my-visits toggle changes
  useEffect(() => {
    if (activeTab === 'traffic') {
      fetchVisitorStats(visitorRange, hideMyVisits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorRange, hideMyVisits]);

  const handleApproveVideo = async (videoId) => {
    try {
      await axiosInstance.post(`/admin/videos/${videoId}/approve`);
      toast.success('Video approved');
      fetchPendingVideos();
      fetchVideos();
    } catch {
      toast.error('Failed to approve video');
    }
  };

  const handleRejectVideo = async (videoId) => {
    try {
      await axiosInstance.post(`/admin/videos/${videoId}/reject`);
      toast.success('Video rejected');
      fetchPendingVideos();
      fetchVideos();
    } catch {
      toast.error('Failed to reject video');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!confirm('Are you sure you want to delete this video?')) return;
    try {
      await axiosInstance.delete(`/videos/${videoId}`);
      toast.success('Video deleted');
      fetchVideos();
    } catch {
      toast.error('Failed to delete video');
    }
  };

  const handleToggleLyric = async (video) => {
    const next = !video.is_lyric;
    try {
      await axiosInstance.post(`/admin/videos/${video.id}/mark-lyric?is_lyric=${next}`);
      toast.success(next ? 'Marked as lyric video — pushed to bottom of feed' : 'Unmarked as lyric video');
      fetchVideos();
    } catch {
      toast.error('Failed to update video');
    }
  };

  // Pull a video into / out of the Featured review queue. Behavior depends on
  // current state — pending shows quick approve/reject, others pull in.
  const handleFeaturedAction = async (video) => {
    if (video.featured) {
      if (!confirm(`Un-feature "${video.title}"? It will be removed from marquee spots.`)) return;
      try {
        await axiosInstance.post(`/admin/videos/${video.id}/feature-reject?reason=${encodeURIComponent('Removed by admin')}`);
        toast.success('Un-featured');
        fetchVideos();
      } catch { toast.error('Failed to un-feature'); }
      return;
    }
    if (video.review_status === 'pending') {
      const choice = window.prompt('Type "approve" to feature, or a rejection reason to reject:', 'approve');
      if (!choice) return;
      try {
        if (choice.trim().toLowerCase() === 'approve') {
          await axiosInstance.post(`/admin/videos/${video.id}/feature-approve`);
          toast.success('Approved — now featured');
        } else {
          await axiosInstance.post(`/admin/videos/${video.id}/feature-reject?reason=${encodeURIComponent(choice.trim())}`);
          toast.success('Rejected from Featured');
        }
        fetchVideos();
      } catch { toast.error('Action failed'); }
      return;
    }
    // not_submitted or rejected — pull into review
    if (!confirm(`Pull "${video.title}" into the Featured review queue?`)) return;
    try {
      await axiosInstance.post(`/admin/videos/${video.id}/feature-pending`);
      toast.success('Pulled into review queue');
      fetchVideos();
    } catch { toast.error('Failed to pull into review'); }
  };

  // Tracks in-flight remux operations so the button can show a spinner per row.
  const [remuxingIds, setRemuxingIds] = useState(() => new Set());

  // Video diagnostic modal — opened from the per-row stethoscope button.
  const [diagnosingId, setDiagnosingId] = useState(null);
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);
  const [diagnoseReport, setDiagnoseReport] = useState(null);
  const [diagnoseError, setDiagnoseError] = useState(null);

  const openDiagnose = async (video) => {
    setDiagnosingId(video.id);
    setDiagnoseReport(null);
    setDiagnoseError(null);
    setDiagnoseLoading(true);
    try {
      const res = await axiosInstance.get(`/admin/videos/${video.id}/diagnose`);
      setDiagnoseReport(res.data);
    } catch (err) {
      setDiagnoseError(err?.response?.data?.detail || err.message || 'Diagnose failed');
    } finally {
      setDiagnoseLoading(false);
    }
  };

  const closeDiagnose = () => {
    setDiagnosingId(null);
    setDiagnoseReport(null);
    setDiagnoseError(null);
    setDiagnoseLoading(false);
  };

  // Poll the videos list a few times after a remux is kicked off so the UI
  // reflects done/failed status without the admin having to manually refresh.
  const pollRemuxStatus = (videoId) => {
    let polls = 0;
    const maxPolls = 30; // ~3 minutes
    const id = setInterval(async () => {
      polls += 1;
      // Match fetchVideos's `limit:1000` — using `limit:100` here truncated
      // the array on every poll tick, silently dropping tail rows (e.g.
      // lyric-flagged videos which sink to the bottom of the ranked feed).
      // That's what caused "video shows in artist library but not in Admin"
      // for `is_lyric:true` rows (see RCA_f4f35c6a-…).
      const fresh = await axiosInstance
        .get('/videos', { params: { limit: 1000, include_all: true } })
        .then(r => r.data)
        .catch(() => null);
      const v = fresh?.find?.((x) => x.id === videoId);
      if (v && v.remux_status !== 'running') {
        setVideos(fresh);
        if (v.remux_status === 'done') {
          toast.success(`"${v.title}" fixed (${v.remux_size_mb} MB). Mobile playback should work now.`);
        } else if (v.remux_status === 'failed') {
          toast.error(`Remux failed: ${v.remux_error || 'Unknown error.'}`);
        }
        clearInterval(id);
      }
      if (polls >= maxPolls) clearInterval(id);
    }, 6000);
  };

  // One-click cleanup of videos that never got their stored file written
  // (placeholders left behind by a dead background task before async-finalize).
  const handleCleanupOrphans = async () => {
    const orphanCount = videos.filter((v) => !hasVideoFile(v) && v.status !== 'processing').length;
    if (orphanCount === 0) {
      toast.success('No orphan videos to clean up.');
      return;
    }
    if (!confirm(`Permanently delete ${orphanCount} broken video placeholder(s) with no stored file? This can't be undone — but those uploads can't be recovered anyway.`)) return;
    try {
      // First call: dry-run (default). Show the user the count + a sample.
      const dry = await axiosInstance.post('/admin/videos/cleanup-orphans');
      const wouldDelete = dry?.data?.would_delete ?? 0;
      if (!wouldDelete) {
        toast.success('No true orphans found by the safety-checked query. Nothing to clean.');
        return;
      }
      // Second confirm (HARD action — requires explicit go).
      const preview = (dry?.data?.preview || []).slice(0, 5).map((p) => `• ${p.title || p.id}`).join('\n');
      if (!confirm(`Server flagged ${wouldDelete} doc(s) as TRUE orphans (no Bunny GUID, no HLS, no file path).\n\nSample:\n${preview || '(no preview)'}\n\nProceed with permanent delete?`)) return;
      const res = await axiosInstance.post('/admin/videos/cleanup-orphans?confirm=true');
      toast.success(`Deleted ${res.data.deleted} true-orphan placeholder(s).`);
      fetchVideos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cleanup failed.');
    }
  };

  // Restore a single soft-deleted video (admin). Used by the "Restore"
  // button that appears next to the DELETED badge on each card.
  const handleRestoreVideo = async (video) => {
    if (!confirm(`Restore "${video.title || video.id}"?`)) return;
    try {
      await axiosInstance.post(`/admin/videos/${video.id}/restore`);
      toast.success('Restored.');
      fetchVideos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed.');
    }
  };

  // Bulk-restore every currently-soft-deleted video. Two-step confirm to
  // avoid an accidental click. Server runs in dry-run mode first.
  const handleRestoreAllDeleted = async () => {
    try {
      const dry = await axiosInstance.post('/admin/videos/restore-all-deleted');
      const wouldRestore = dry?.data?.would_restore ?? 0;
      if (!wouldRestore) {
        toast.info('No soft-deleted videos to restore.');
        return;
      }
      const preview = (dry?.data?.preview || [])
        .slice(0, 8)
        .map((p) => `• ${p.title || p.id} — by ${p.owner?.username || '?'}`)
        .join('\n');
      if (!confirm(`Restore ${wouldRestore} soft-deleted video(s)?\n\nSample:\n${preview}\n\nOwners stay attached. You can re-delete any that shouldn't be back.`)) return;
      const res = await axiosInstance.post('/admin/videos/restore-all-deleted?confirm=true');
      toast.success(`Restored ${res.data.restored} video(s). ${res.data.status_reset ? `${res.data.status_reset} also had status reset to approved.` : ''}`);
      fetchVideos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk restore failed.');
    }
  };

  // Reassign a video to a different artist account. Prompts for username,
  // calls the admin reassign endpoint, refreshes the list.
  const handleReassignVideo = async (video) => {
    const current = video.username ? `@${video.username}` : '(unknown)';
    const target = window.prompt(`Reassign "${video.title}" — currently ${current}.\n\nEnter the NEW artist's username (case-insensitive) or email:`);
    if (!target || !target.trim()) return;
    const looksLikeEmail = target.includes('@');
    const body = looksLikeEmail ? { email: target.trim() } : { username: target.trim() };
    try {
      const res = await axiosInstance.post(`/admin/videos/${video.id}/reassign`, body);
      if (res.data?.no_change) {
        toast.info('Already owned by that user — no change.');
        return;
      }
      toast.success(`Reassigned to @${res.data?.new_owner?.username}.`);
      fetchVideos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reassign failed.');
    }
  };

  // Rebuild Mongo records from the Bunny library. RELINKS any blank rows
  // whose title matches a Bunny video, AND INSERTS new rows for any Bunny
  // video that has no audivue match at all. Dry-run by default.
  const handleRebuildFromBunny = async () => {
    try {
      const dry = await axiosInstance.post('/admin/videos/rebuild-from-bunny', {});
      const wouldRelink = dry?.data?.would_relink ?? 0;
      const wouldInsert = dry?.data?.would_insert ?? 0;
      const librarySize = dry?.data?.bunny_library_size ?? 0;
      const linked = dry?.data?.already_linked ?? 0;
      if (!wouldRelink && !wouldInsert) {
        toast.info(`Bunny library has ${librarySize} video(s); ${linked} are already linked. Nothing to rebuild.`);
        return;
      }
      const relinkPrev = (dry?.data?.relink_preview || [])
        .slice(0, 6)
        .map((p) => `• ${p.title} → fills missing bunny_video_guid on existing row`)
        .join('\n');
      const insertPrev = (dry?.data?.insert_preview || [])
        .slice(0, 6)
        .map((p) => `• ${p.title} (NEW row — will be assigned to you; re-attribute via per-video edit)`)
        .join('\n');
      const msg = `Bunny library: ${librarySize} video(s). ${linked} already linked.\n\nWill RELINK ${wouldRelink} existing blank row(s):\n${relinkPrev || '(none)'}\n\nWill INSERT ${wouldInsert} new row(s):\n${insertPrev || '(none)'}\n\nProceed?`;
      if (!confirm(msg)) return;
      const res = await axiosInstance.post('/admin/videos/rebuild-from-bunny?confirm=true', {});
      toast.success(`Rebuild done — relinked ${res.data.relinked}, inserted ${res.data.inserted}.`);
      fetchVideos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rebuild failed. Check that Bunny env vars are set on the pod.');
    }
  };

  // Re-warm Facebook/LinkedIn share previews for every approved video.
  // Useful for videos published before the auto-warming hook landed.
  const handleWarmAllSocialCaches = async () => {
    if (!confirm('Ping Facebook & LinkedIn to refresh share previews for every approved video? Takes a few seconds.')) return;
    try {
      const res = await axiosInstance.post('/admin/videos/warm-social-cache-all');
      toast.success(`Cache-warm started for ${res.data.warmed} video(s). FB/LinkedIn will refresh in 30-60s.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Warm failed.');
    }
  };

  // Bulk re-run the moderation pipeline on every rejected video. Useful after
  // a moderation prompt revision so creators don't have to re-upload. The
  // backend queues per-video re-fetches as background tasks — we just kick
  // it off and refresh the grid a moment later.
  const [reModerating, setReModerating] = useState(false);
  const handleReModerateRejected = async () => {
    const count = filteredVideos.length;
    if (count === 0) {
      toast.info('No rejected videos to re-moderate.');
      return;
    }
    if (!confirm(
      `Re-run moderation against ${count} rejected video(s)? Each one will be re-downloaded and re-checked against the current moderation rules in the background. Videos that now pass will flip to Approved automatically.`
    )) return;
    setReModerating(true);
    try {
      const res = await axiosInstance.post('/admin/videos/re-moderate-rejected');
      toast.success(
        `Queued ${res.data.queued} video(s) for re-moderation. ` +
        (res.data.skipped_no_file ? `Skipped ${res.data.skipped_no_file} with no stored file. ` : '') +
        'Refresh in 1–2 minutes to see results.'
      );
      // Give the first few re-runs a moment to settle then refresh the list.
      setTimeout(() => { fetchVideos(); }, 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Re-moderation failed to start.');
    } finally {
      setReModerating(false);
    }
  };

  // Per-video share-preview refresh. Pings Facebook + LinkedIn to re-scrape
  // THIS specific URL — fixes the case where FB has a stale generic preview
  // cached from before the OG meta tags were correct.
  const handleWarmOne = async (video) => {
    try {
      await axiosInstance.post(`/admin/videos/${video.id}/warm-social-cache`);
      toast.success(`FB/LinkedIn re-scrape requested. Preview should refresh in 30-60s — re-share to test.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refresh failed.');
    }
  };

  // Kick off a fix-mobile-playback (+faststart remux) on a stored video.
  // The backend runs the work in a background task and updates the video doc
  // with `remux_status` so we can poll for completion.
  const handleFaststartRemux = async (video) => {
    if (remuxingIds.has(video.id) || video.remux_status === 'running') return;
    if (!confirm(`Fix mobile playback for "${video.title}"? Takes 1–2 minutes on large files. Runs in the background.`)) return;
    setRemuxingIds((prev) => {
      const next = new Set(prev); next.add(video.id); return next;
    });
    try {
      await axiosInstance.post(`/admin/videos/${video.id}/faststart-remux`);
      toast.success('Remux started. Refreshing in a moment…');
      fetchVideos();
      pollRemuxStatus(video.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remux failed to start.');
    } finally {
      setRemuxingIds((prev) => {
        const next = new Set(prev); next.delete(video.id); return next;
      });
    }
  };

  // Kick off a full re-encode (h264 ≤70MB) for oversized uploads. Use this
  // when an artist's source file is 200+ MB and streams too slowly on mobile.
  const handleRecompress = async (video) => {
    if (remuxingIds.has(video.id) || video.remux_status === 'running' || video.remux_status === 'recompressing') return;
    if (!confirm(`Re-encode "${video.title}" to ≤70 MB? Takes 1–10 minutes depending on length. Runs in the background.`)) return;
    setRemuxingIds((prev) => { const next = new Set(prev); next.add(video.id); return next; });
    try {
      await axiosInstance.post(`/admin/videos/${video.id}/recompress`);
      toast.success('Recompress started. Refreshing in a moment…');
      fetchVideos();
      pollRemuxStatus(video.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Recompress failed to start.');
    } finally {
      setRemuxingIds((prev) => { const next = new Set(prev); next.delete(video.id); return next; });
    }
  };

  const handleBanUser = async (userId) => {
    if (!confirm('Are you sure you want to ban this user?')) return;
    try {
      await axiosInstance.post(`/admin/users/${userId}/ban`);
      toast.success('User banned');
      fetchUsers();
    } catch {
      toast.error('Failed to ban user');
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await axiosInstance.post(`/admin/users/${userId}/unban`);
      toast.success('User unbanned');
      fetchUsers();
    } catch {
      toast.error('Failed to unban user');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedItems.length === 0) {
      toast.error('No items selected');
      return;
    }
    try {
      await Promise.all(selectedItems.map(id => axiosInstance.post(`/admin/videos/${id}/approve`)));
      toast.success(`${selectedItems.length} videos approved`);
      setSelectedItems([]);
      fetchPendingVideos();
    } catch {
      toast.error('Failed to approve some videos');
    }
  };

  const handleBulkReject = async () => {
    if (selectedItems.length === 0) {
      toast.error('No items selected');
      return;
    }
    try {
      await Promise.all(selectedItems.map(id => axiosInstance.post(`/admin/videos/${id}/reject`)));
      toast.success(`${selectedItems.length} videos rejected`);
      setSelectedItems([]);
      fetchPendingVideos();
    } catch {
      toast.error('Failed to reject some videos');
    }
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = (items) => {
    const ids = items.map(i => i.id);
    setSelectedItems(prev => 
      prev.length === ids.length ? [] : ids
    );
  };

  // All admin timestamps are rendered in Central Time so audit trails are
  // consistent for whoever opens the dashboard, regardless of their browser
  // locale.
  const CT_TZ = 'America/Chicago';

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      timeZone: CT_TZ,
    }) + ' CT';
  };

  const formatDateOnly = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      timeZone: CT_TZ,
    });
  };

  const formatTimeCT = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      timeZone: CT_TZ,
    }) + ' CT';
  };

  // Date filter helper
  const isWithinDateRange = (dateStr) => {
    if (dateFilter === 'all') return true;
    const date = new Date(dateStr);
    const now = new Date();
    const dayInMs = 24 * 60 * 60 * 1000;
    
    switch (dateFilter) {
      case 'today':
        return (now - date) < dayInMs;
      case 'week':
        return (now - date) < (7 * dayInMs);
      case 'month':
        return (now - date) < (30 * dayInMs);
      default:
        return true;
    }
  };

  // Filtered data with all filters applied
  const filteredUsers = users.filter(u => {
    const matchesSearch = u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.username?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDate = isWithinDateRange(u.created_at);
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && !u.is_banned) ||
      (statusFilter === 'banned' && u.is_banned);
    const matchesRole = roleFilter === 'all' || (u.role || 'listener') === roleFilter;
    return matchesSearch && matchesDate && matchesStatus && matchesRole;
  });

  const filteredVideos = videos.filter(v => {
    const matchesSearch = v.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.artist?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDate = isWithinDateRange(v.created_at);
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'deleted' ? v.is_deleted === true :
       statusFilter === 'active' ? !v.is_deleted :
       v.status === statusFilter);
    const matchesCategory = categoryFilter === 'all' || 
      (v.genre || '').toLowerCase() === categoryFilter.toLowerCase();
    return matchesSearch && matchesDate && matchesStatus && matchesCategory;
  });

  const filteredPendingVideos = pendingVideos.filter(v => {
    const matchesSearch = v.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.artist?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDate = isWithinDateRange(v.created_at);
    return matchesSearch && matchesDate;
  });

  // Refresh data
  const refreshData = async () => {
    setLoading(true);
    setLastRefresh(new Date());
    await fetchAllData();
    toast.success('Data refreshed');
  };

  // Reset filters
  const resetFilters = () => {
    setDateFilter('all');
    setStatusFilter('active');
    setCategoryFilter('all');
    setRoleFilter('all');
    setSearchQuery('');
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-zinc-500">You don't have admin privileges.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-black text-white pb-24"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* Header */}
      <div className="border-b border-zinc-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-sm text-zinc-500">
              Last updated: {formatTimeCT(lastRefresh)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && activeTab === 'users' && (
              <CopyEmailsButton users={filteredUsers} />
            )}
            {isAdmin && (
              <button
                onClick={handleExportUsers}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium transition-colors"
                data-testid="admin-export-users-btn"
                title="Download all users as CSV"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export Users (CSV)</span>
                <span className="sm:hidden">Export</span>
              </button>
            )}
            {/* Ghost-artist outreach — filters to artists with 0 uploads in
                the last 30 days and generates a CSV with a pre-written,
                personalized re-engagement message per artist. Ready to
                paste into any mailer. Only surfaces on the Users tab. */}
            {isAdmin && activeTab === 'users' && (
              <button
                onClick={handleExportGhostArtists}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-sm font-medium transition-colors"
                data-testid="admin-export-ghost-artists-btn"
                title="CSV of artists who signed up but haven't uploaded — includes a ready-to-send re-engagement message per artist"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Ghost Artists (CSV)</span>
                <span className="sm:hidden">Ghosts</span>
              </button>
            )}
            <button 
              onClick={refreshData}
              className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
              data-testid="admin-refresh"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 py-4 border-b border-zinc-800 overflow-x-auto">
        <div className="flex gap-2" style={{ scrollbarWidth: 'none' }}>
          <TabButton 
            active={activeTab === 'overview'} 
            onClick={() => setActiveTab('overview')}
            icon={BarChart3}
            label="Overview"
          />
          <TabButton 
            active={activeTab === 'invites'} 
            onClick={() => setActiveTab('invites')}
            icon={Key}
            label="Invite Codes"
            count={inviteCodes.length}
          />
          <TabButton 
            active={activeTab === 'upgrades'} 
            onClick={() => setActiveTab('upgrades')}
            icon={ArrowUpCircle}
            label="Upgrades"
            count={upgradeRequests.length}
          />
          <TabButton 
            active={activeTab === 'moderation'} 
            onClick={() => setActiveTab('moderation')}
            icon={Shield}
            label="Moderation"
            count={pendingVideos.length}
          />
          <TabButton 
            active={activeTab === 'users'} 
            onClick={() => setActiveTab('users')}
            icon={Users}
            label="Users"
            count={users.length}
          />
          <TabButton 
            active={activeTab === 'videos'} 
            onClick={() => setActiveTab('videos')}
            icon={Video}
            label="Videos"
            count={videos.filter(v => !v.is_deleted).length}
          />
          <TabButton
            active={activeTab === 'seed'}
            onClick={() => setActiveTab('seed')}
            icon={Sparkles}
            label="Seed"
          />
          <TabButton
            active={activeTab === 'featured-queue'}
            onClick={() => setActiveTab('featured-queue')}
            icon={Star}
            label="Featured Queue"
          />
          <TabButton
            active={activeTab === 'upload-events'}
            onClick={() => setActiveTab('upload-events')}
            icon={Upload}
            label="Upload Events"
          />
          <TabButton
            active={activeTab === 'ab-like-prompt'}
            onClick={() => setActiveTab('ab-like-prompt')}
            icon={TrendingUp}
            label="A/B: Like Prompt"
          />
          <TabButton 
            active={activeTab === 'revenue'} 
            onClick={() => setActiveTab('revenue')}
            icon={DollarSign}
            label="Revenue"
          />
          <TabButton 
            active={activeTab === 'activity'} 
            onClick={() => setActiveTab('activity')}
            icon={Activity}
            label="Activity"
          />
          <TabButton 
            active={activeTab === 'traffic'} 
            onClick={() => setActiveTab('traffic')}
            icon={Globe}
            label="Traffic"
          />
          <TabButton 
            active={activeTab === 'flags'} 
            onClick={() => setActiveTab('flags')}
            icon={Zap}
            label="Feature Flags"
          />
          <TabButton 
            active={activeTab === 'platform-analytics'} 
            onClick={() => setActiveTab('platform-analytics')}
            icon={BarChart3}
            label="Platform Analytics"
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Overview Tab — single simplified analytics surface */}
        {activeTab === 'overview' && (
          <AdminOverviewTab
            adminApiError={adminApiError}
            channelStats={channelStats}
            fetchFunnel={fetchFunnel}
            funnel={funnel}
            funnelLoading={funnelLoading}
            pendingVideos={pendingVideos}
            setActiveTab={setActiveTab}
            stats={stats}
            users={users}
            videos={videos}
            visitorStats={visitorStats}
          />
        )}

        {/* Invite Codes Tab */}
        {activeTab === 'invites' && (
          <AdminInvitesTab
            codeLoading={codeLoading}
            copyInviteLink={copyInviteLink}
            createInviteCode={createInviteCode}
            deleteInviteCode={deleteInviteCode}
            formatDateOnly={formatDateOnly}
            inviteCodes={inviteCodes}
            newCode={newCode}
            newMaxUses={newMaxUses}
            newRoleType={newRoleType}
            setNewCode={setNewCode}
            setNewMaxUses={setNewMaxUses}
            setNewRoleType={setNewRoleType}
            toggleCodeActive={toggleCodeActive}
          />
        )}

        {/* Upgrade Requests Tab */}
        {activeTab === 'upgrades' && (
          <AdminUpgradesTab
            formatDate={formatDate}
            handleApproveUpgrade={handleApproveUpgrade}
            handleRejectUpgrade={handleRejectUpgrade}
            upgradeRequests={upgradeRequests}
            user={user}
            videos={videos}
          />
        )}

        {/* Moderation Tab */}
        {activeTab === 'moderation' && (
          <AdminModerationTab
            dateFilter={dateFilter}
            filteredPendingVideos={filteredPendingVideos}
            handleApproveVideo={handleApproveVideo}
            handleBulkApprove={handleBulkApprove}
            handleBulkReject={handleBulkReject}
            handleRejectVideo={handleRejectVideo}
            resetFilters={resetFilters}
            searchQuery={searchQuery}
            selectAll={selectAll}
            selectedItems={selectedItems}
            setDateFilter={setDateFilter}
            setPreviewVideo={setPreviewVideo}
            setSearchQuery={setSearchQuery}
            toggleSelectItem={toggleSelectItem}
            videos={videos}
          />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <AdminUsersTab
            dateFilter={dateFilter}
            filteredUsers={filteredUsers}
            handleBanUser={handleBanUser}
            handleSetUserRole={handleSetUserRole}
            handleUnbanUser={handleUnbanUser}
            navigate={navigate}
            resetFilters={resetFilters}
            roleFilter={roleFilter}
            searchQuery={searchQuery}
            setDateFilter={setDateFilter}
            setRoleFilter={setRoleFilter}
            setSearchQuery={setSearchQuery}
            setStatusFilter={setStatusFilter}
            statusFilter={statusFilter}
            user={user}
            users={users}
          />
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <AdminVideosTab
            categoryFilter={categoryFilter}
            dateFilter={dateFilter}
            filteredVideos={filteredVideos}
            handleApproveVideo={handleApproveVideo}
            handleCleanupOrphans={handleCleanupOrphans}
            handleDeleteVideo={handleDeleteVideo}
            handleFaststartRemux={handleFaststartRemux}
            handleFeaturedAction={handleFeaturedAction}
            handleReModerateRejected={handleReModerateRejected}
            handleReassignVideo={handleReassignVideo}
            handleRebuildFromBunny={handleRebuildFromBunny}
            handleRestoreAllDeleted={handleRestoreAllDeleted}
            handleRestoreVideo={handleRestoreVideo}
            handleToggleLyric={handleToggleLyric}
            handleWarmAllSocialCaches={handleWarmAllSocialCaches}
            handleWarmOne={handleWarmOne}
            openDiagnose={openDiagnose}
            reModerating={reModerating}
            remuxingIds={remuxingIds}
            resetFilters={resetFilters}
            searchQuery={searchQuery}
            setCategoryFilter={setCategoryFilter}
            setDateFilter={setDateFilter}
            setPreviewVideo={setPreviewVideo}
            setSearchQuery={setSearchQuery}
            setStatusFilter={setStatusFilter}
            statusFilter={statusFilter}
            videos={videos}
          />
        )}

        {/* Seed Tab — admin-only demo creators */}
        {activeTab === 'seed' && (
          <SeedTab axiosInstance={axiosInstance} />
        )}

        {/* Featured Queue Tab — manual quality gate for marquee placement */}
        {activeTab === 'featured-queue' && (
          <FeaturedQueueTab token={token} />
        )}

        {/* Upload Events Tab — attempt→completion funnel + raw event log */}
        {activeTab === 'upload-events' && (
          <UploadEventsTab token={token} />
        )}

        {/* A/B: Like Prompt — funnel of the like-auth modal copy test */}
        {activeTab === 'ab-like-prompt' && (
          <LikePromptAbTab axiosInstance={axiosInstance} />
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <AdminRevenueTab
          />
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <AdminActivityTab
            formatDate={formatDate}
            loginActivity={loginActivity}
          />
        )}

        {/* Traffic Tab */}
        {activeTab === 'traffic' && (
          <AdminTrafficTab
            fetchVisitorStats={fetchVisitorStats}
            hideMyVisits={hideMyVisits}
            scrubBotVisits={scrubBotVisits}
            setHideMyVisits={setHideMyVisits}
            setVisitorRange={setVisitorRange}
            users={users}
            visitorRange={visitorRange}
            visitorStats={visitorStats}
          />
        )}

        {/* Feature Flags Tab */}
        {activeTab === 'flags' && (
          <AdminFlagsTab
            featureFlags={featureFlags}
            flagCategoryFilter={flagCategoryFilter}
            flagEnvironment={flagEnvironment}
            flagHistory={flagHistory}
            formatDate={formatDate}
            resetFeatureFlag={resetFeatureFlag}
            rollbackFeatureFlag={rollbackFeatureFlag}
            setFlagCategoryFilter={setFlagCategoryFilter}
            toggleFeatureFlag={toggleFeatureFlag}
          />
        )}

        {/* Platform Analytics Tab — embeds the full AnalyticsPage component */}
        {activeTab === 'platform-analytics' && (
          <AdminPlatformAnalyticsTab
          />
        )}
      </div>

      {/* Video Preview Modal — admin can watch flagged/pending videos before moderating */}
      {previewVideo && (
        <AdminVideoPreviewModal
          handleApproveVideo={handleApproveVideo}
          handleRejectVideo={handleRejectVideo}
          previewVideo={previewVideo}
          setPreviewVideo={setPreviewVideo}
        />
      )}

      {/* Video Diagnose Modal — deep ffprobe + moov atom scan from Object Storage */}
      {diagnosingId && (
        <AdminVideoDiagnoseModal
          closeDiagnose={closeDiagnose}
          diagnoseError={diagnoseError}
          diagnoseLoading={diagnoseLoading}
          diagnoseReport={diagnoseReport}
          diagnosingId={diagnosingId}
          handleFaststartRemux={handleFaststartRemux}
          handleRecompress={handleRecompress}
          videos={videos}
        />
      )}
    </div>
  );
};
